import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Search, RotateCcw, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtIDR } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { CustomerSearchSelect } from "../components/CustomerSearchSelect";

export default function ShippingSale() {
  const navigate = useNavigate();
  const [number, setNumber] = useState("—");
  const [groups, setGroups] = useState([]);
  const [channels, setChannels] = useState([]);
  const [form, setForm] = useState({ group_id: "", customer_id: "", marketplace: "", order_id: "", note: "" });
  const [selCust, setSelCust] = useState(null);
  const [selectedKeep, setSelectedKeep] = useState(null);
  const [rows, setRows] = useState([]);
  const [discType, setDiscType] = useState("none");
  const [discValue, setDiscValue] = useState("");
  const [pickOpen, setPickOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/cashier/next-number").then((r) => setNumber(r.data.number)).catch(() => setNumber("SALES—"));
    api.get("/customer-groups", { params: { active: "active" } }).then((r) => setGroups(r.data)).catch(() => {});
    api.get("/settings/marketplace-channels").then((r) => setChannels(r.data.channels || [])).catch(() => {});
  }, []);

  const subtotal = useMemo(() => rows.reduce((s, r) => s + (parseInt(r.price) || 0) * (parseInt(r.qty) || 0), 0), [rows]);
  const discountAmt = useMemo(() => {
    const v = parseInt(discValue) || 0;
    if (discType === "percent") return Math.min(Math.round(subtotal * Math.min(v, 100) / 100), subtotal);
    if (discType === "amount") return Math.min(v, subtotal);
    return 0;
  }, [discType, discValue, subtotal]);
  const grandTotal = subtotal - discountAmt;

  const isKeep = !!selectedKeep;
  const grpName = isKeep ? (selectedKeep?.group_name || "") : (groups.find((g) => g.id === form.group_id)?.name || "");
  const applyKeep = async (k) => {
    setSelectedKeep(k);
    setRows((k.items || []).map((it) => ({
      variant_id: it.variant_id, product_name: it.product_name, color: it.color, size: it.size,
      warehouse_id: it.warehouse_id, warehouse_name: it.warehouse_name, keep_item_id: it.keep_item_id,
      price: it.price, qty: it.qty_remaining, max: it.qty_remaining,
    })));
    let mp = "";
    if (k.customer_id) {
      try { const r = await api.get(`/customers/${k.customer_id}`); mp = r.data?.marketplace || ""; } catch (e) { /* ignore */ }
      setSelCust({ id: k.customer_id, name: k.customer_name, marketplace: mp });
    } else { setSelCust(null); }
    setForm((f) => ({ ...f, group_id: k.group_id || "", customer_id: k.customer_id || "", marketplace: mp || f.marketplace }));
    toast.success(`Keep ${k.number} dimuat${mp ? ` · channel ${mp}` : ""}`);
  };
  const clearKeep = () => { setSelectedKeep(null); setRows([]); setSelCust(null); setForm((f) => ({ ...f, group_id: "", customer_id: "" })); };

  const setRow = (i, patch) => { const n = [...rows]; n[i] = { ...n[i], ...patch }; setRows(n); };
  const setRowQty = (v, qty) => setRows((rs) => {
    const idx = rs.findIndex((r) => r.variant_id === v.variant_id);
    if (qty <= 0) return rs.filter((r) => r.variant_id !== v.variant_id);
    if (idx === -1) return [...rs, { ...v, qty }];
    const n = [...rs]; n[idx] = { ...n[idx], qty }; return n;
  });
  const reset = () => { setSelectedKeep(null); setForm({ group_id: "", customer_id: "", marketplace: "", order_id: "", note: "" }); setSelCust(null); setRows([]); setDiscType("none"); setDiscValue(""); };

  const save = async () => {
    if (saving) return;
    if (!isKeep && !form.group_id) return toast.error("Pilih Group");
    if (!isKeep && !form.customer_id) return toast.error("Pilih Customer");
    if (!form.marketplace) return toast.error("Pilih Marketplace Channel");
    if (!form.order_id.trim()) return toast.error("Isi Order ID Marketplace");
    if (isKeep && rows.some((r) => (parseInt(r.qty) || 0) > (r.max || 0))) return toast.error("Qty melebihi sisa Keep");
    const items = rows.filter((r) => r.variant_id && parseInt(r.qty) > 0).map((r) => ({
      variant_id: r.variant_id, warehouse_id: r.warehouse_id, qty: parseInt(r.qty) || 0,
      price: parseInt(r.price) || 0, keep_item_id: r.keep_item_id || null,
    }));
    if (!items.length) return toast.error("Tambahkan minimal satu produk");
    const grp = groups.find((g) => g.id === form.group_id);
    const payload = {
      mode: isKeep ? "keep" : "direct", keep_id: isKeep ? selectedKeep.keep_id : null,
      customer_id: form.customer_id || (isKeep ? selectedKeep.customer_id : null),
      customer_name: selCust?.name || (isKeep ? selectedKeep.customer_name : ""),
      group_id: isKeep ? (selectedKeep.group_id || form.group_id) : form.group_id,
      group_name: isKeep ? (selectedKeep.group_name || "") : (grp?.name || ""),
      channel: "Shipping Online", term_of_payment: "Marketplace", note: form.note, items,
      marketplace: form.marketplace, order_id: form.order_id.trim(),
      discount_type: discType, discount_value: parseInt(discValue) || 0,
      payment_method: "", cash_received: 0,
      idempotency_key: `SHIP-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    };
    setSaving(true);
    try {
      const r = await api.post("/cashier/sale", payload);
      toast.success(`Shipping Sale ${r.data.number} tersimpan (Unpaid — dilunasi via Settlement)`);
      navigate("/sales");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSaving(false); }
  };

  return (
    <div data-testid="shipping-sale-page">
      <PageHeader title="Shipping Sale" subtitle="Kasir online — penjualan marketplace tanpa pembayaran (dilunasi saat settlement)" />

      <Panel className="p-5 mb-4">
        <div className="text-sm text-cocoa mb-4">NO SALE : <span className="font-mono font-bold text-status-passed" data-testid="ship-sale-number">{number}</span></div>
        <div className="mb-4">
          <Label>Keep Code (opsional)</Label>
          <KeepCodeSearch selectedKeep={selectedKeep} onSelect={applyKeep} onClear={clearKeep} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Group *</Label>
            {isKeep ? (
              <Input value={grpName || "-"} disabled className="bg-taupe/10" data-testid="ship-group-readonly" />
            ) : (
              <Select value={form.group_id || undefined} onValueChange={(v) => { setForm({ ...form, group_id: v, customer_id: "" }); setSelCust(null); }}>
                <SelectTrigger className="bg-white" data-testid="ship-group-select"><SelectValue placeholder="Select Group" /></SelectTrigger>
                <SelectContent className="max-h-72">{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <div>
            <Label>Customer *</Label>
            {isKeep ? (
              <Input value={selectedKeep.customer_name || "-"} disabled className="bg-taupe/10" data-testid="ship-customer-readonly" />
            ) : (
              <CustomerSearchSelect groupId={form.group_id} selected={selCust} testid="ship-customer-search"
                onSelect={(c) => { setSelCust(c); setForm((f) => ({ ...f, customer_id: c.id, marketplace: c.marketplace || f.marketplace })); }}
                onClear={() => { setSelCust(null); setForm((f) => ({ ...f, customer_id: "" })); }} />
            )}
          </div>
          <div>
            <Label>Marketplace Channel *</Label>
            <Select value={form.marketplace || undefined} onValueChange={(v) => setForm({ ...form, marketplace: v })}>
              <SelectTrigger className="bg-white" data-testid="ship-marketplace-select"><SelectValue placeholder="Select Marketplace" /></SelectTrigger>
              <SelectContent>{channels.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div><Label>Order ID Marketplace *</Label><Input value={form.order_id} onChange={(e) => setForm({ ...form, order_id: e.target.value })} placeholder="Enter Order ID" className="bg-white" data-testid="ship-order-id-input" /></div>
        </div>
      </Panel>

      <Panel className="mb-4">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-12">No</TableHead><TableHead>Product Name</TableHead>
              <TableHead>Color</TableHead><TableHead>Size</TableHead>
              <TableHead className="text-right">Price (@)</TableHead>
              <TableHead className="text-center w-24">Qty</TableHead>
              <TableHead className="text-right">Total</TableHead><TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={`${r.variant_id}-${r.warehouse_id}`} data-testid={`ship-row-${i}`}>
                <TableCell>{i + 1}</TableCell>
                <TableCell className="font-medium text-cocoa">{r.product_name}<div className="text-[11px] text-wmuted">{r.warehouse_name}</div></TableCell>
                <TableCell>{r.color}</TableCell>
                <TableCell>{r.size}</TableCell>
                <TableCell className="text-right"><Input type="number" className="w-28 text-right ml-auto" value={r.price} onChange={(e) => setRow(i, { price: e.target.value })} data-testid={`ship-row-price-${i}`} /></TableCell>
                <TableCell className="text-center"><Input type="number" className="w-20 text-center mx-auto" value={r.qty} onChange={(e) => setRow(i, { qty: e.target.value })} data-testid={`ship-row-qty-${i}`} /></TableCell>
                <TableCell className="text-right font-medium">{fmtIDR((parseInt(r.price) || 0) * (parseInt(r.qty) || 0))}</TableCell>
                <TableCell>{!isKeep && <Button size="icon" variant="ghost" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`ship-row-del-${i}`}><Trash2 className="w-4 h-4 text-status-reject" /></Button>}</TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={8} className="text-center text-wmuted py-8">{isKeep ? "Keep tanpa sisa item." : "Belum ada produk."}</TableCell></TableRow>}
          </TableBody>
        </Table>
        {!isKeep && (
          <div className="flex items-center justify-center py-4 border-t border-taupe/15">
            <Button onClick={() => setPickOpen(true)} data-testid="ship-add-product-btn" className="bg-blue-600 hover:bg-blue-700 text-white"><Plus className="w-4 h-4 mr-1.5" /> Add Product</Button>
          </div>
        )}
      </Panel>

      <Panel className="p-5 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <Label>Diskon</Label>
            <div className="flex gap-2">
              <Select value={discType} onValueChange={setDiscType}>
                <SelectTrigger className="bg-white w-32" data-testid="ship-disc-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Tanpa</SelectItem>
                  <SelectItem value="amount">Rp</SelectItem>
                  <SelectItem value="percent">%</SelectItem>
                </SelectContent>
              </Select>
              <Input type="number" value={discValue} onChange={(e) => setDiscValue(e.target.value)} disabled={discType === "none"} className="bg-white" placeholder="0" data-testid="ship-disc-value" />
            </div>
          </div>
          <div className="md:col-span-2 flex items-end justify-end">
            <div className="text-sm space-y-1 w-full max-w-xs">
              <div className="flex justify-between"><span className="text-wmuted">Subtotal</span><span data-testid="ship-subtotal">{fmtIDR(subtotal)}</span></div>
              {discountAmt ? <div className="flex justify-between text-status-reject"><span>Diskon</span><span>- {fmtIDR(discountAmt)}</span></div> : null}
              <div className="flex justify-between font-bold text-cocoa text-base pt-1 border-t border-taupe/20"><span>Grand Total</span><span data-testid="ship-grandtotal">{fmtIDR(grandTotal)}</span></div>
              <div className="flex justify-between text-status-reject text-xs pt-1"><span>Status Bayar</span><span data-testid="ship-payment-note">Unpaid (menunggu settlement)</span></div>
            </div>
          </div>
        </div>
        <Label>Sale Note</Label>
        <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} data-testid="ship-note-input" className="bg-white" />
      </Panel>

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" onClick={reset} className="border-status-reject/40 text-status-reject" data-testid="ship-reset-btn"><RotateCcw className="w-4 h-4 mr-1.5" /> Reset</Button>
        <Button onClick={save} disabled={saving || !rows.length} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="ship-save-btn"><Save className="w-4 h-4 mr-1.5" /> {saving ? "Menyimpan..." : "Save"}</Button>
      </div>

      <ShipProductPicker open={pickOpen} setOpen={setPickOpen} rows={rows} onSetQty={setRowQty} />
    </div>
  );
}

function ShipProductPicker({ open, setOpen, rows, onSetQty }) {
  const [q, setQ] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const doSearch = async (term) => {
    setLoading(true);
    try {
      const r = await api.get("/cashier/recent-products", { params: { q: term || undefined } });
      setProducts((r.data.products || []).map((p) => ({ ...p, variants: (p.variants || []).map((v) => ({ ...v, total: (v.warehouses || []).reduce((s, w) => s + (w.available || 0), 0) })) })));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) { setQ(""); doSearch(""); } /* eslint-disable-next-line */ }, [open]);
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length === 0) { doSearch(""); return; }
    if (term.length < 2) { setProducts([]); return; }
    const t = setTimeout(() => doSearch(term), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, open]);
  const qtyOf = (vid) => rows.find((r) => r.variant_id === vid)?.qty || 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Add Product</DialogTitle></DialogHeader>
        <div className="mb-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ketik nama produk / SKU (otomatis)..." className="pl-9 bg-white" data-testid="ship-product-search" autoFocus />
            {loading && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-wmuted">Mencari...</span>}
          </div>
        </div>
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Color</TableHead><TableHead>Size</TableHead><TableHead className="text-center">Total Stock</TableHead><TableHead className="text-right">Price</TableHead><TableHead className="text-center w-44">Action</TableHead></TableRow></TableHeader>
          <TableBody>
            {products.map((p) => (
              <React.Fragment key={p.product_id}>
                <TableRow className="hover:bg-transparent bg-taupe/10"><TableCell colSpan={5} className="font-semibold text-cocoa py-2">{p.product_name}</TableCell></TableRow>
                {p.variants.map((v) => {
                  const cur = qtyOf(v.variant_id);
                  const vobj = { variant_id: v.variant_id, product_id: p.product_id, product_name: p.product_name, color: v.color, size: v.size, price: v.price, available: v.total };
                  return (
                    <TableRow key={v.variant_id} data-testid={`ship-pick-row-${v.variant_id}`}>
                      <TableCell className="text-sm">{v.color}</TableCell>
                      <TableCell className="text-sm">{v.size}</TableCell>
                      <TableCell className="text-center font-medium">{v.total}</TableCell>
                      <TableCell className="text-right text-sm">{fmtIDR(v.price)}</TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-1">
                          <Button size="icon" className="h-8 w-8 bg-blue-600 hover:bg-blue-700 text-white" disabled={cur <= 0} onClick={() => onSetQty(vobj, Math.max(0, cur - 1))} data-testid={`ship-pick-minus-${v.variant_id}`}>−</Button>
                          <Input type="number" className="w-16 h-8 text-center" value={cur || ""} onChange={(e) => onSetQty(vobj, Math.max(0, parseInt(e.target.value) || 0))} data-testid={`ship-pick-qty-${v.variant_id}`} />
                          <Button size="icon" className="h-8 w-8 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => onSetQty(vobj, cur + 1)} data-testid={`ship-pick-plus-${v.variant_id}`}>+</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </React.Fragment>
            ))}
            {!products.length && <TableRow><TableCell colSpan={5} className="text-center text-wmuted py-8">{loading ? "Mencari..." : "Ketik minimal 2 huruf, hasil tampil otomatis."}</TableCell></TableRow>}
          </TableBody>
        </Table>
        <DialogFooter><Button onClick={() => setOpen(false)} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="ship-pick-done-btn">Selesai</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function KeepCodeSearch({ selectedKeep, onSelect, onClear }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (selectedKeep) return;
    const term = q.trim();
    if (term.length < 1) { setItems([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try { const r = await api.get("/cashier/keep-search", { params: { q: term } }); setItems(r.data.items || []); setOpen(true); }
      catch (e) { /* ignore */ }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [q, selectedKeep]);

  if (selectedKeep) {
    return (
      <div className="flex items-center gap-2 bg-status-passed/10 border border-status-passed/30 rounded-md px-3 py-2" data-testid="ship-keep-selected-box">
        <span className="font-mono font-bold text-cocoa" data-testid="ship-selected-keep-number">{selectedKeep.number}</span>
        <span className="text-sm text-wmuted">· {selectedKeep.customer_name || "-"}{selectedKeep.group_name ? ` · ${selectedKeep.group_name}` : ""} · {selectedKeep.items.length} item</span>
        <Button size="icon" variant="ghost" onClick={onClear} className="ml-auto h-7 w-7" data-testid="ship-keep-clear-btn"><X className="w-4 h-4 text-status-reject" /></Button>
      </div>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => items.length && setOpen(true)}
        placeholder="Search by Keep Code or Order ID" className="pl-9 bg-white" data-testid="ship-keep-code-input" />
      {loading && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-wmuted">...</span>}
      {open && items.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-taupe/30 rounded-md shadow-lg max-h-72 overflow-y-auto" data-testid="ship-keep-suggestions">
          {items.map((k) => (
            <button key={k.keep_id} onClick={() => { onSelect(k); setOpen(false); setQ(""); }} data-testid={`ship-keep-suggest-${k.keep_id}`}
              className="w-full text-left px-3 py-2 hover:bg-taupe/10 border-b border-taupe/10 last:border-0">
              <div className="flex items-center justify-between">
                <span className="font-mono font-semibold text-cocoa">{k.number}</span>
                <span className="text-[10px] bg-taupe/20 px-1.5 py-0.5 rounded">{k.status}</span>
              </div>
              <div className="text-xs text-wmuted">{k.customer_name || "-"}{k.group_name ? ` · ${k.group_name}` : ""} · {k.items.length} item</div>
            </button>
          ))}
        </div>
      )}
      {open && !loading && q.trim() && !items.length && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-taupe/30 rounded-md shadow-lg px-3 py-2 text-sm text-wmuted">Keep tidak ditemukan.</div>
      )}
    </div>
  );
}
